import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../../App';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Loader2, Plus, Trash2, Edit, UserPlus, BookOpen, Languages } from 'lucide-react';

const TranslationBooksManagement = () => {
  const [books, setBooks] = useState([]);
  const [translators, setTranslators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [selectedBook, setSelectedBook] = useState(null);
  const [saving, setSaving] = useState(false);
  
  const [formData, setFormData] = useState({
    book_title: '',
    original_author: '',
    original_language: 'Engels',
    target_language: 'Afrikaans',
    page_count: '',
    description: '',
    genre: '',
    difficulty_level: 'Medium',
    deadline_days: ''
  });

  useEffect(() => {
    fetchBooks();
    fetchTranslators();
  }, []);

  const fetchBooks = async () => {
    try {
      const response = await axios.get(`${API}/admin/translation-books`);
      setBooks(response.data);
    } catch (error) {
      toast.error('Kon nie boeke laai nie');
    } finally {
      setLoading(false);
    }
  };

  const fetchTranslators = async () => {
    try {
      const response = await axios.get(`${API}/admin/users`);
      const activeTranslators = response.data.filter(u => u.role === 'translator' && u.status === 'active');
      setTranslators(activeTranslators);
    } catch (error) {
      console.error('Failed to fetch translators:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      book_title: '',
      original_author: '',
      original_language: 'Engels',
      target_language: 'Afrikaans',
      page_count: '',
      description: '',
      genre: '',
      difficulty_level: 'Medium',
      deadline_days: ''
    });
    setSelectedBook(null);
  };

  const handleSubmit = async () => {
    if (!formData.book_title || !formData.original_author || !formData.page_count) {
      toast.error('Vul alle verpligte velde in');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...formData,
        page_count: parseInt(formData.page_count),
        deadline_days: formData.deadline_days ? parseInt(formData.deadline_days) : null
      };

      if (selectedBook) {
        await axios.put(`${API}/admin/translation-books/${selectedBook.id}`, payload);
        toast.success('Boek suksesvol opgedateer');
      } else {
        await axios.post(`${API}/admin/translation-books`, payload);
        toast.success('Boek suksesvol bygevoeg');
      }
      
      setShowAddDialog(false);
      resetForm();
      fetchBooks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kon nie boek stoor nie');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (book) => {
    setSelectedBook(book);
    setFormData({
      book_title: book.book_title,
      original_author: book.original_author,
      original_language: book.original_language || 'Engels',
      target_language: book.target_language || 'Afrikaans',
      page_count: book.page_count?.toString() || '',
      description: book.description || '',
      genre: book.genre || '',
      difficulty_level: book.difficulty_level || 'Medium',
      deadline_days: book.deadline_days?.toString() || ''
    });
    setShowAddDialog(true);
  };

  const handleDelete = async (bookId) => {
    if (!window.confirm('Is jy seker jy wil hierdie boek verwyder?')) return;
    
    try {
      await axios.delete(`${API}/admin/translation-books/${bookId}`);
      toast.success('Boek suksesvol verwyder');
      fetchBooks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kon nie boek verwyder nie');
    }
  };

  const handleAssign = async (translatorId) => {
    if (!selectedBook || !translatorId) return;
    
    try {
      await axios.post(`${API}/admin/translation-books/${selectedBook.id}/assign/${translatorId}`);
      toast.success('Boek suksesvol toegeken');
      setShowAssignDialog(false);
      setSelectedBook(null);
      fetchBooks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kon nie boek toeken nie');
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'available': { label: 'Beskikbaar', class: 'bg-green-100 text-green-700' },
      'assigned': { label: 'Toegeken', class: 'bg-blue-100 text-blue-700' },
      'in_progress': { label: 'Besig', class: 'bg-orange-100 text-orange-700' },
      'completed': { label: 'Voltooi', class: 'bg-gray-100 text-gray-700' }
    };
    const config = statusConfig[status] || statusConfig['available'];
    return <Badge className={config.class}>{config.label}</Badge>;
  };

  const getDifficultyBadge = (level) => {
    const config = {
      'Easy': { label: 'Maklik', class: 'bg-green-50 text-green-600 border-green-200' },
      'Medium': { label: 'Medium', class: 'bg-yellow-50 text-yellow-600 border-yellow-200' },
      'Hard': { label: 'Moeilik', class: 'bg-red-50 text-red-600 border-red-200' }
    };
    const c = config[level] || config['Medium'];
    return <Badge variant="outline" className={c.class}>{c.label}</Badge>;
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-3xl font-bold text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            Vertaling Boeke
          </h2>
          <p className="text-gray-600">Bestuur boeke beskikbaar vir vertaling</p>
        </div>
        <Dialog open={showAddDialog} onOpenChange={(open) => { setShowAddDialog(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="bg-orange-500 hover:bg-orange-600" data-testid="add-translation-book-btn">
              <Plus className="mr-2 h-4 w-4" /> Voeg Boek By
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{selectedBook ? 'Wysig Boek' : 'Nuwe Vertaling Boek'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label>Boek Titel *</Label>
                  <Input
                    value={formData.book_title}
                    onChange={(e) => setFormData({ ...formData, book_title: e.target.value })}
                    placeholder="bv. The Great Gatsby"
                    data-testid="book-title-input"
                  />
                </div>
                <div className="col-span-2">
                  <Label>Oorspronklike Skrywer *</Label>
                  <Input
                    value={formData.original_author}
                    onChange={(e) => setFormData({ ...formData, original_author: e.target.value })}
                    placeholder="bv. F. Scott Fitzgerald"
                    data-testid="book-author-input"
                  />
                </div>
                <div>
                  <Label>Oorspronklike Taal</Label>
                  <Select
                    value={formData.original_language}
                    onValueChange={(value) => setFormData({ ...formData, original_language: value })}
                  >
                    <SelectTrigger data-testid="original-language-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Engels">Engels</SelectItem>
                      <SelectItem value="Frans">Frans</SelectItem>
                      <SelectItem value="Duits">Duits</SelectItem>
                      <SelectItem value="Spaans">Spaans</SelectItem>
                      <SelectItem value="Italiaans">Italiaans</SelectItem>
                      <SelectItem value="Ander">Ander</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Teiken Taal</Label>
                  <Select
                    value={formData.target_language}
                    onValueChange={(value) => setFormData({ ...formData, target_language: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Afrikaans">Afrikaans</SelectItem>
                      <SelectItem value="Engels">Engels</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Bladsy Telling *</Label>
                  <Input
                    type="number"
                    value={formData.page_count}
                    onChange={(e) => setFormData({ ...formData, page_count: e.target.value })}
                    placeholder="bv. 250"
                    data-testid="book-pages-input"
                  />
                </div>
                <div>
                  <Label>Genre</Label>
                  <Input
                    value={formData.genre}
                    onChange={(e) => setFormData({ ...formData, genre: e.target.value })}
                    placeholder="bv. Roman, Nie-fiksie"
                  />
                </div>
                <div>
                  <Label>Moeilikheidsgraad</Label>
                  <Select
                    value={formData.difficulty_level}
                    onValueChange={(value) => setFormData({ ...formData, difficulty_level: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Easy">Maklik</SelectItem>
                      <SelectItem value="Medium">Medium</SelectItem>
                      <SelectItem value="Hard">Moeilik</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Spertyd (Dae)</Label>
                  <Input
                    type="number"
                    value={formData.deadline_days}
                    onChange={(e) => setFormData({ ...formData, deadline_days: e.target.value })}
                    placeholder="bv. 30"
                  />
                </div>
                <div className="col-span-2">
                  <Label>Beskrywing</Label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={3}
                    placeholder="Kort beskrywing van die boek..."
                  />
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => { setShowAddDialog(false); resetForm(); }}>
                  Kanselleer
                </Button>
                <Button 
                  onClick={handleSubmit} 
                  disabled={saving}
                  className="bg-orange-500 hover:bg-orange-600"
                  data-testid="save-book-btn"
                >
                  {saving ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Besig...</> : 'Stoor'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-green-100 rounded-lg">
                <BookOpen className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Beskikbaar</p>
                <p className="text-2xl font-bold">{books.filter(b => b.status === 'available').length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-blue-100 rounded-lg">
                <UserPlus className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Toegeken</p>
                <p className="text-2xl font-bold">{books.filter(b => b.status === 'assigned').length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-orange-100 rounded-lg">
                <Languages className="h-6 w-6 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Besig</p>
                <p className="text-2xl font-bold">{books.filter(b => b.status === 'in_progress').length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-gray-100 rounded-lg">
                <BookOpen className="h-6 w-6 text-gray-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Totaal</p>
                <p className="text-2xl font-bold">{books.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Books List */}
      <Card>
        <CardHeader>
          <CardTitle>Alle Vertaling Boeke</CardTitle>
        </CardHeader>
        <CardContent>
          {books.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <BookOpen className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <p>Geen vertaling boeke nog nie</p>
              <p className="text-sm">Voeg 'n boek by sodat vertalers dit kan kies</p>
            </div>
          ) : (
            <div className="space-y-4">
              {books.map((book) => (
                <div 
                  key={book.id} 
                  className="p-4 border rounded-lg hover:border-orange-200 transition-colors"
                  data-testid={`translation-book-${book.id}`}
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold text-lg">{book.book_title}</h3>
                        {getStatusBadge(book.status)}
                        {getDifficultyBadge(book.difficulty_level)}
                      </div>
                      <p className="text-gray-600">deur {book.original_author}</p>
                      <div className="flex gap-4 mt-2 text-sm text-gray-500">
                        <span>{book.original_language} → {book.target_language}</span>
                        <span>{book.page_count} bladsye</span>
                        {book.genre && <span>{book.genre}</span>}
                        {book.deadline_days && <span>{book.deadline_days} dae</span>}
                      </div>
                      {book.description && (
                        <p className="mt-2 text-sm text-gray-600">{book.description}</p>
                      )}
                      {book.translator_name && (
                        <p className="mt-2 text-sm">
                          <span className="text-gray-500">Toegeken aan:</span>{' '}
                          <span className="font-medium text-blue-600">{book.translator_name}</span>
                        </p>
                      )}
                    </div>
                    <div className="flex gap-2">
                      {book.status === 'available' && (
                        <>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => { setSelectedBook(book); setShowAssignDialog(true); }}
                            data-testid={`assign-book-${book.id}`}
                          >
                            <UserPlus className="h-4 w-4 mr-1" /> Toeken
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleEdit(book)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDelete(book.id)}
                            className="text-red-600 hover:bg-red-50"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Assign Dialog */}
      <Dialog open={showAssignDialog} onOpenChange={setShowAssignDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Ken Boek Toe aan Vertaler</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            {selectedBook && (
              <div className="mb-4 p-3 bg-gray-50 rounded">
                <p className="font-medium">{selectedBook.book_title}</p>
                <p className="text-sm text-gray-600">deur {selectedBook.original_author}</p>
              </div>
            )}
            <Label>Kies Vertaler</Label>
            {translators.length === 0 ? (
              <p className="text-sm text-gray-500 mt-2">Geen aktiewe vertalers beskikbaar nie</p>
            ) : (
              <div className="space-y-2 mt-2 max-h-60 overflow-y-auto">
                {translators.map((translator) => (
                  <div
                    key={translator.id}
                    className="p-3 border rounded hover:border-orange-300 cursor-pointer transition-colors"
                    onClick={() => handleAssign(translator.id)}
                  >
                    <p className="font-medium">{translator.voornaam} {translator.van}</p>
                    <p className="text-sm text-gray-500">{translator.email}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TranslationBooksManagement;
